import { useEffect, useState } from 'react';
import { usePwaInstall } from '@/shared/hooks/usePwaInstall';
import { useI18n } from '@/hooks/useI18n';

const DISMISS_KEY = 'pwa:install-dismissed-at';
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* storage disabled — fall through */
  }
}

export function PwaInstallPrompt() {
  const { t } = useI18n();
  const { canInstall, isIOS, isStandalone, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(isRecentlyDismissed);
  const [iosModalOpen, setIosModalOpen] = useState(false);

  useEffect(() => {
    if (isStandalone) setDismissed(true);
  }, [isStandalone]);

  const shouldShow = !isStandalone && !dismissed && (canInstall || isIOS);
  if (!shouldShow) return null;

  const onInstallClick = async () => {
    if (isIOS) {
      setIosModalOpen(true);
      return;
    }
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      setDismissed(true);
    } else if (outcome === 'dismissed') {
      markDismissed();
      setDismissed(true);
    }
  };

  const onDismiss = () => {
    markDismissed();
    setDismissed(true);
  };

  return (
    <>
      <div
        className="fixed left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+72px)] md:left-auto md:right-4 md:bottom-4 md:max-w-sm z-[65] bg-white rounded-2xl shadow-xl border border-slate-200 p-4 flex items-center gap-3"
        role="dialog"
        aria-label={t('pwa.install_title')}
      >
        <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{t('pwa.install_title')}</div>
          <div className="text-xs text-slate-500 mt-0.5">{t('pwa.install_desc')}</div>
        </div>
        <button
          type="button"
          onClick={onInstallClick}
          className="flex-shrink-0 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 active:bg-primary-700 px-3 py-2 rounded-lg"
        >
          {t('pwa.install_cta')}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('pwa.dismiss')}
          className="flex-shrink-0 text-slate-400 hover:text-slate-600 -mr-1 p-1"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
      </div>

      {iosModalOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-end md:items-center justify-center p-4"
          onClick={() => setIosModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ios-install-title"
          >
            <h3 id="ios-install-title" className="text-base font-semibold text-slate-900 mb-3">
              {t('pwa.ios_title')}
            </h3>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center">1</span>
                <span className="flex-1">
                  {t('pwa.ios_step1_prefix')}
                  <span className="inline-flex items-center justify-center w-6 h-6 mx-1 align-middle text-primary-500">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3v12" />
                      <path d="M8 7l4-4 4 4" />
                      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
                    </svg>
                  </span>
                  {t('pwa.ios_step1_suffix')}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center">2</span>
                <span className="flex-1">{t('pwa.ios_step2')}</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center">3</span>
                <span className="flex-1">{t('pwa.ios_step3')}</span>
              </li>
            </ol>
            <button
              type="button"
              onClick={() => {
                markDismissed();
                setIosModalOpen(false);
                setDismissed(true);
              }}
              className="mt-5 w-full text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 py-2.5 rounded-lg"
            >
              {t('pwa.got_it')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
